/**
 * SSE Performance Tracking Hook
 *
 * Listens to Redux state for SSE performance metrics and fires Statsig events.
 * This enables alerting when streaming responses exceed performance thresholds.
 */

import { useEffect, useRef } from 'react';
import { useEventTracking } from '@services/statsig';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import { clearSSEPerformanceMetrics } from '../store/chatSlice';
import { selectSSEPerformanceMetrics } from '../store/selectors';

/**
 * Hook that tracks SSE performance metrics by listening to Redux state
 * and forwarding them to Statsig for analytics and alerting.
 *
 * This hook should be used in a component that renders when the note-ai-edit
 * feature is active (e.g., NoteChatView or NoteView).
 *
 * @param noteId - The note ID for context (required for tracking)
 */
export const useSSEPerformanceTracking = (noteId: string) => {
  const dispatch = useAppDispatch();
  const { note } = useEventTracking();
  const metrics = useAppSelector(selectSSEPerformanceMetrics);
  const previousMetricsRef = useRef<typeof metrics>(null);

  useEffect(() => {
    // Only track if metrics exist and are different from previous (avoid duplicate tracking)
    if (metrics && metrics !== previousMetricsRef.current) {
      // Fire Statsig event with performance metrics
      note.chatSSEPerformance(noteId, {
        time_to_connection_ms: metrics.time_to_connection_ms,
        time_to_first_event_ms: metrics.time_to_first_event_ms,
        time_to_first_token_ms: metrics.time_to_first_token_ms,
        total_duration_ms: metrics.total_duration_ms,
        run_id: metrics.run_id,
        thread_id: metrics.thread_id,
        outcome: metrics.outcome,
        event_count: metrics.event_count,
        exceeded_threshold: metrics.exceeded_threshold,
        threshold_ms: metrics.threshold_ms,
      });

      // Clear metrics after tracking to prevent duplicate events
      dispatch(clearSSEPerformanceMetrics());

      // Clear ref to prevent memory leak from persisting metrics objects
      previousMetricsRef.current = null;
    }
  }, [metrics, noteId, dispatch, note]);
};

